import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { DevLoginForm } from './DevLoginForm';

const ERROR_MESSAGES: Record<string, string> = {
  timeout: "L'accesso ha impiegato troppo tempo. Riprova.",
};

export function LoginPage() {
  const { user, signInWithGoogle, error: authError, clearError } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Read error from URL params (set by AuthCallback)
  useEffect(() => {
    const urlError = searchParams.get('error');
    if (urlError) {
      setLocalError(ERROR_MESSAGES[urlError] || decodeURIComponent(urlError));
      // Clear the URL param
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const displayError = localError || authError;

  // Con Google si passa da /auth/callback, che reindirizza. Il login con
  // password no: senza questo si resta fermi sulla pagina di accesso pur
  // essendo dentro.
  if (user) return <Navigate to="/" replace />;

  const handleSignIn = async () => {
    setIsLoading(true);
    setLocalError(null);
    clearError();
    try {
      await signInWithGoogle();
    } catch {
      setLocalError('Errore durante il login. Riprova.');
    } finally {
      // Note: if OAuth redirect succeeds, this component unmounts
      // so the finally block won't cause issues
      setIsLoading(false);
    }
  };


  return (
    <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
      <div className="flex flex-col items-center gap-8 animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
            <span className="text-2xl font-bold text-white">Z</span>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text-primary">Zenda</h1>
            <p className="text-sm text-text-quaternary mt-1">Gestione progetti intelligente</p>
          </div>
        </div>

        {/* Error message */}
        {displayError && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 max-w-xs">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-sm text-red-400">{displayError}</p>
          </div>
        )}

        {/* Google Sign In */}
        <button
          onClick={handleSignIn}
          disabled={isLoading}
          className="flex items-center gap-3 px-6 py-3 rounded-lg bg-white text-gray-800 font-medium text-sm hover:bg-gray-50 transition-colors shadow-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          )}
          {isLoading ? 'Accesso in corso...' : 'Accedi con Google'}
        </button>

        {/* Solo in sviluppo: login con email e password.
            Il componente sta in un modulo a parte così la build di produzione
            lo elimina del tutto, credenziali di prova comprese. */}
        {import.meta.env.DEV && (
          <DevLoginForm
            disabled={isLoading}
            onStart={() => { setIsLoading(true); setLocalError(null); clearError(); }}
            onEnd={() => setIsLoading(false)}
            onError={setLocalError}
          />
        )}
      </div>
    </div>
  );
}
